import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { setCurrentUser } from "../../../redux/reducers/userReducer";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import SignupLoading from "./SignupLoading";
import { css } from "../../../../styled-system/css";
import { apiClient } from "../../../zodios/client";
import type { PatchOnboardData } from "../../../zodios/apiDefinitions/api/onboarding";
import type { AxiosError } from "axios";

/** Saves an "about you" onboarding answer to the backend, then calls `onSaved`. */
export function useSaveOnboardAnswer({ onSaved }: { onSaved: () => void }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: async (answer: PatchOnboardData) => {
      return await apiClient.patchOnboardData(answer);
    },
    onSuccess: (_res, answer) => {
      // Keep redux user state up to date with fields updated in backend
      dispatch(setCurrentUser(answer));
      onSaved();
    },
    onError: (error: AxiosError) => {
      // If user loses tokens, send back to sign in page
      if (error.response?.status === 401) {
        void navigate({ to: "/" });
      }
    },
  });
}

/** Frame shared by the "about you" onboarding questions: the greeting, the question, and its answer cards. */
export function AboutQuestion({
  prompt,
  isSaving,
  children,
}: {
  prompt: string;
  isSaving: boolean;
  children: ReactNode;
}) {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation(["onboarding"]);

  const userFirstName = user.user_full_name.split(" ")[0] ?? user.user_full_name;

  return (
    <VStack
      css={{
        alignItems: "flex-start",
        width: "100%",
        maxWidth: { base: "100%", lg: "66.666%" },
        marginY: "xlarge",
        marginX: "auto",
        textAlign: "start",
      }}
    >
      <h2
        className={css({
          marginTop: "xlarge",
          marginBottom: "xsmall",
          fontWeight: "600",
        })}
      >
        {t(($) => $.onboarding["Welcome {name}!"], { name: userFirstName })}
      </h2>
      <p className={css({ fontSize: "xlarge" })}>
        {t(($) => $.onboarding["We have a few questions to ensure that you get the most out of Trellis"])}
      </p>
      <h4 className={css({ fontWeight: "500" })}>{prompt}</h4>
      {isSaving ?
        <Flex className={css({ justifyContent: "center", width: "100%", paddingY: "xlarge" })}>
          <SignupLoading />
        </Flex>
      : <VStack width="100%">{children}</VStack>}
    </VStack>
  );
}
